import { sha256 } from '@noble/hashes/sha256';
import { xchacha20poly1305 } from '@noble/ciphers/chacha';
import { bytesToHex, hexToBytes, b64encode, b64decode, randomBytes, isHex32, tagValue } from './util.js';
import { KIND } from './kinds.js';

/**
 * Attachments: content-addressed blobs carried outside the event.
 *
 * An event body is capped at 256 KiB, so a PDF or an image cannot live inside
 * it. The blob is stored separately and referenced by `sha256(bytes)`.
 *
 * The hash sits inside the signed event (for a broadcast) or inside the signed
 * ciphertext (for a directed message), so integrity needs no second signature:
 * a client hashes what it downloaded and compares. A relay cannot substitute
 * content, and a corrupted blob is detected rather than rendered.
 *
 * For directed messages the blob is encrypted once with a random key, and that
 * key travels inside the per-recipient ciphertext. The relay stores ciphertext
 * and never holds the key -- and one encryption serves any number of
 * recipients, so a statement sent to many people is not re-encrypted per person.
 */

export const BLOB_LIMITS = {
  maxBytes: 10 * 1024 * 1024,
  maxPerEvent: 10,
};

export function hashBlob(bytes) {
  return bytesToHex(sha256(bytes));
}

/** @returns {{ciphertext:Uint8Array, key:string, hash:string}} key is base64 */
export function encryptBlob(bytes) {
  const key = randomBytes(32);
  const nonce = randomBytes(24);
  const sealed = xchacha20poly1305(key, nonce).encrypt(bytes);
  const ciphertext = new Uint8Array(24 + sealed.length);
  ciphertext.set(nonce, 0);
  ciphertext.set(sealed, 24);
  return { ciphertext, key: b64encode(key), hash: hashBlob(ciphertext) };
}

export function decryptBlob(ciphertext, keyB64) {
  if (ciphertext.length < 25) throw new Error('blob too short');
  const key = b64decode(keyB64);
  if (key.length !== 32) throw new Error('blob key must be 32 bytes');
  const nonce = ciphertext.subarray(0, 24);
  return xchacha20poly1305(key, nonce).decrypt(ciphertext.subarray(24));
}

/**
 * Validate a descriptor before it is signed or after it is received.
 * @returns {string|null} an error message, or null when acceptable
 */
export function checkAttachment(attachment) {
  if (!attachment || typeof attachment !== 'object') return 'attachment must be an object';
  if (!isHex32(attachment.hash)) return 'attachment hash must be 32-byte hex';
  if (!attachment.filename || typeof attachment.filename !== 'string') return 'attachment needs a filename';
  if (attachment.filename.length > 255) return 'filename too long';
  // The filename is used in a download prompt, so it must not be a path.
  if (/[/\\]|^\.\.?$/.test(attachment.filename)) return 'filename must not contain path separators';
  if (!/^[a-z]+\/[a-z0-9.+-]+$/i.test(attachment.mime ?? '')) return 'attachment needs a valid mime type';
  if (!Number.isInteger(attachment.size) || attachment.size < 0) return 'attachment size must be a non-negative integer';
  if (attachment.size > BLOB_LIMITS.maxBytes) return `attachment exceeds ${BLOB_LIMITS.maxBytes} bytes`;
  return null;
}

/** Public attachment descriptors live in tags: hash, filename, mime, size. */
export function attachmentTag({ hash, filename, mime, size }) {
  return ['attachment', hash, filename, mime, String(size)];
}

export function parseAttachmentTags(event) {
  return (event.tags ?? [])
    .filter((t) => t[0] === 'attachment' && t.length >= 5)
    .map((t) => ({ hash: t[1], filename: t[2], mime: t[3], size: Number(t[4]), key: null }))
    .filter((a) => checkAttachment(a) === null);
}

/**
 * NIP-98 HTTP authorisation.
 *
 * The signed event binds the method, the URL and (for requests with a body)
 * the payload hash, so a captured header cannot be replayed against a
 * different request. The `Nostr` scheme name is NIP-98's; the relay advertises
 * NIP-98 support, so it has to be the real thing rather than a lookalike.
 */
export const HTTP_AUTH_KIND = 27235;
export const HTTP_AUTH_SCHEME = 'Nostr';

export function buildHttpAuth({ url, method = 'GET', payloadHash = null }, secretKey, signEvent) {
  const tags = [['u', url], ['method', method]];
  if (payloadHash) tags.push(['payload', payloadHash]);
  const event = signEvent({ kind: HTTP_AUTH_KIND, content: '', tags }, secretKey);
  return `${HTTP_AUTH_SCHEME} ${b64encode(new TextEncoder().encode(JSON.stringify(event)))}`;
}

/** Kept for the blob upload path, which always carries a payload. */
export function buildUploadAuth({ url, method = 'PUT', payloadHash }, secretKey, signEvent) {
  return buildHttpAuth({ url, method, payloadHash }, secretKey, signEvent);
}

/**
 * @returns {{ok:true, event:object}|{ok:false, error:string}}
 */
export function parseUploadAuth(header, { url, method, payloadHash = null, checkEvent, now: nowFn }) {
  const prefix = `${HTTP_AUTH_SCHEME} `;
  if (typeof header !== 'string' || !header.startsWith(prefix)) {
    return { ok: false, error: `missing ${HTTP_AUTH_SCHEME} authorization header` };
  }
  let event;
  try {
    event = JSON.parse(new TextDecoder().decode(b64decode(header.slice(prefix.length))));
  } catch {
    return { ok: false, error: 'authorization header is not a valid encoded event' };
  }
  const structural = checkEvent(event);
  if (!structural.ok) return { ok: false, error: `authorization event invalid: ${structural.error}` };
  if (event.kind !== HTTP_AUTH_KIND) return { ok: false, error: 'wrong authorization event kind' };
  if (Math.abs(nowFn() - event.created_at) > 60) return { ok: false, error: 'authorization event expired' };
  if (tagValue(event, 'method') !== method) return { ok: false, error: 'authorization is for a different method' };
  if (payloadHash !== null && tagValue(event, 'payload') !== payloadHash) {
    return { ok: false, error: 'authorization does not match the uploaded bytes' };
  }

  const authorized = tagValue(event, 'u');
  // Compare only path and host: a proxy may rewrite the scheme (ws/http vs wss/https).
  try {
    const a = new URL(authorized);
    const b = new URL(url);
    if (a.host !== b.host || a.pathname !== b.pathname) {
      return { ok: false, error: 'authorization is for a different URL' };
    }
  } catch {
    return { ok: false, error: 'authorization URL is unparseable' };
  }
  return { ok: true, event };
}

/** A relay's HTTP origin, derived from its WebSocket URL. */
export function httpBaseFromRelayUrl(relayUrl) {
  return String(relayUrl).replace(/^ws:/i, 'http:').replace(/^wss:/i, 'https:').replace(/\/$/, '');
}

/** Kinds that may reference attachments. */
export function allowsAttachments(kind) {
  return kind === KIND.BROADCAST || kind === KIND.DIRECTED;
}
